/**
 * Servicio para gestionar Services (infraestructura)
 * Nota: Este servicio es diferente del servicio de API base
 */
import { apiService } from './api';
import { AnyService } from '../types/service.type';
import { AppLogsResponse, DeployHistoryResponse, DeployRecord } from '../types/deploys.type';

export type { DeployRecord, AppLogsResponse };

export interface ServiceListResponse {
  services: AnyService[];
  total: number;
  page: number;
  limit: number;
}

export interface BackupRecoveryPoint {
  recoveryPointArn: string;
  createdAt: string | null;
  status: string;
  sizeBytes: number;
  vault: string;
}

export interface CreateServiceDto {
  name: string;
  type: AnyService['type'];
  instanceType?: string;
  region?: string;
  environment?: 'production' | 'staging' | 'development';
  tags?: string[];
  config?: Record<string, any>;
}

export interface UpdateServiceDto {
  name?: string;
  instanceType?: string;
  region?: string;
  environment?: 'production' | 'staging' | 'development';
  tags?: string[];
  config?: Record<string, any>;
}

/**
 * Estado de provisión / DNS del dominio personalizado (lo emite el backend).
 *  - WAITING_DNS: el dominio fue registrado pero el usuario aún no configura el CNAME.
 *  - VALIDATING: DNS detectado, esperando validación del certificado.
 *  - PROVISIONING: cert validado, configurando ALB/CloudFront.
 *  - ACTIVE: dominio activo y sirviendo tráfico.
 *  - FAILED: algo falló (DNS, certificado, etc.)
 */
export type CustomDomainStatus = 'WAITING_DNS' | 'VALIDATING' | 'PROVISIONING' | 'ACTIVE' | 'FAILED' | string;

/** Estado del certificado ACM */
export type CustomDomainCertStatus = 'PENDING_VALIDATION' | 'ISSUED' | 'FAILED' | 'REVOKED' | string;

/** Un registro DNS que el usuario debe crear en su proveedor */
export interface CustomDomainDnsRecord {
  type: string;   // p.ej. 'CNAME'
  name: string;
  value: string;
}

/**
 * Representa un dominio personalizado registrado en un servicio App Deploy.
 * El backend devuelve DOS registros CNAME:
 *  - certificateValidation → para que ACM emita el certificado
 *  - traffic              → para enrutar el tráfico al ALB/CloudFront
 */
export interface CustomDomain {
  /** Identificador único del dominio (lo asigna el backend) */
  domainId: string;
  /** Nombre del dominio personalizado del usuario, ej: "crm.miempresa.com" */
  domainName: string;
  /** Estado del dominio */
  status: CustomDomainStatus;
  /** Estado del certificado ACM */
  certificateStatus: CustomDomainCertStatus;
  /** ARN del certificado ACM (cuando aplica) */
  certificateArn?: string;
  /** Host de destino del servicio (a donde apunta el CNAME de tráfico) */
  targetDomain: string;
  /** App a la que pertenece el dominio (si el backend lo envía) */
  appId?: string;
  /** Instrucciones de DNS: dos registros CNAME que debe crear el usuario */
  dnsInstructions: {
    certificateValidation: CustomDomainDnsRecord;
    traffic: CustomDomainDnsRecord;
  };
  /** Fecha de creación */
  createdAt?: string | Date;
  /** Última actualización del estado */
  updatedAt?: string | Date;
  /** Mensaje informativo del backend (ej: "Configure both DNS records and then click Verify Domain.") */
  message?: string;
  /** Último mensaje de error (cuando status = FAILED) */
  errorMessage?: string | null;
}

/** Respuesta del GET /services/{id}/custom-domain */
export interface CustomDomainsListResponse {
  count: number;
  items: CustomDomain[];
}

/**
 * El verify de static (tras emitir certificado) puede devolver `dns` plano
 * en lugar de `dnsInstructions.traffic`:
 *   { type, name, target }  →  traffic.value = target
 */
type CustomDomainVerifyDns = {
  type?: string;
  name?: string;
  value?: string;
  target?: string;
};

type CustomDomainRaw = Partial<CustomDomain> & {
  dns?: CustomDomainVerifyDns;
  dnsInstructions?: {
    certificateValidation?: Partial<CustomDomainDnsRecord> & { target?: string };
    traffic?: Partial<CustomDomainDnsRecord> & { target?: string };
  };
};

const emptyDnsRecord = (): CustomDomainDnsRecord => ({ type: 'CNAME', name: '', value: '' });

/** Normaliza un registro DNS (acepta `value` o `target` como destino del CNAME). */
export function normalizeDnsRecord(
  record?: Partial<CustomDomainDnsRecord> & { target?: string } | null
): CustomDomainDnsRecord {
  if (!record) return emptyDnsRecord();
  return {
    type: record.type || 'CNAME',
    name: record.name || '',
    value: record.value || record.target || '',
  };
}

/**
 * Unifica respuestas del verify y del GET en el shape que consume la UI.
 * Preserva registros previos cuando la respuesta parcial no los trae.
 */
export function normalizeCustomDomain(
  raw: CustomDomainRaw,
  existing?: CustomDomain | null
): CustomDomain {
  const certFromRaw = raw.dnsInstructions?.certificateValidation
    ? normalizeDnsRecord(raw.dnsInstructions.certificateValidation)
    : null;
  const trafficFromRaw = raw.dnsInstructions?.traffic
    ? normalizeDnsRecord(raw.dnsInstructions.traffic)
    : null;
  const trafficFromFlatDns = raw.dns ? normalizeDnsRecord(raw.dns) : null;

  const certificateValidation =
    certFromRaw && (certFromRaw.name || certFromRaw.value)
      ? certFromRaw
      : existing?.dnsInstructions?.certificateValidation ?? emptyDnsRecord();

  const trafficCandidate = trafficFromRaw?.value
    ? trafficFromRaw
    : trafficFromFlatDns?.value
      ? trafficFromFlatDns
      : null;

  const traffic =
    trafficCandidate && (trafficCandidate.name || trafficCandidate.value)
      ? trafficCandidate
      : existing?.dnsInstructions?.traffic ?? emptyDnsRecord();

  const flatTarget = raw.dns?.target ?? raw.dns?.value;

  return {
    domainId: raw.domainId ?? existing?.domainId ?? '',
    domainName: raw.domainName ?? existing?.domainName ?? '',
    status: (raw.status ?? existing?.status ?? 'WAITING_DNS') as CustomDomainStatus,
    certificateStatus: (raw.certificateStatus ??
      existing?.certificateStatus ??
      'PENDING_VALIDATION') as CustomDomainCertStatus,
    certificateArn: raw.certificateArn ?? existing?.certificateArn,
    targetDomain: raw.targetDomain ?? flatTarget ?? existing?.targetDomain ?? traffic.value ?? '',
    appId: raw.appId ?? existing?.appId,
    dnsInstructions: {
      certificateValidation,
      traffic,
    },
    createdAt: raw.createdAt ?? existing?.createdAt,
    updatedAt: raw.updatedAt ?? existing?.updatedAt,
    message: raw.message ?? existing?.message,
    errorMessage: raw.errorMessage ?? existing?.errorMessage,
  };
}

/** Fusiona un dominio actualizado conservando DNS que la respuesta parcial no incluya. */
export function mergeCustomDomain(existing: CustomDomain, updated: CustomDomain): CustomDomain {
  return normalizeCustomDomain(updated, existing);
}

export interface ApiKey {
  token: string;
  tokenInfo: {
    PK?: string;
    SK?: string;
    tenantId?: string;
    serviceId?: string;
    appId?: string;
    tokenId?: string;
    name?: string;
    scope?: string;
    status: 'active' | 'revoked';
    GSI1PK?: string;
    GSI1SK?: string;
    GSI2PK?: string;
    GSI2SK?: string;
    createdAt: string | Date;
    createdBy?: string;
    lastUsedAt?: string | Date | null;
    expiresAt?: number | null; // Timestamp numérico
  };
}

export interface MetricSeries {
  label: string;
  unit: string;
  timestamps: string[];
  values: number[];
  current: number | null;
  max: number | null;
  min: number | null;
  error?: string;
}

export interface ServiceMetrics {
  serviceId: string;
  serviceType: string;
  instanceId?: string;
  hasMetrics: boolean;
  message?: string;
  rangeHours?: number;
  periodSeconds?: number;
  metrics: Record<string, MetricSeries>;
}

export class ServicesService {
  private baseEndpoint = '/services';

  /**
   * Obtiene todos los servicios
   */
  async getAll(params?: {
    page?: number;
    limit?: number;
    type?: AnyService['type'];
    status?: AnyService['status'];
    environment?: AnyService['environment'];
  }): Promise<ServiceListResponse> {
    const queryParams = new URLSearchParams();

    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.type) queryParams.append('type', params.type);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.environment) queryParams.append('environment', params.environment);

    const queryString = queryParams.toString();
    const endpoint = queryString ? `${this.baseEndpoint}?${queryString}` : this.baseEndpoint;

    return apiService.get<ServiceListResponse>(endpoint);
  }

  /**
   * Obtiene un servicio por ID
   */
  async getById(id: string): Promise<AnyService> {
    return apiService.get<AnyService>(`${this.baseEndpoint}/${id}`);
  }

  /**
   * Crea un nuevo servicio
   */
  async create(data: CreateServiceDto): Promise<AnyService> {
    return apiService.post<AnyService>(this.baseEndpoint, data);
  }

  /**
   * Actualiza un servicio
   */
  async update(id: string, data: UpdateServiceDto): Promise<AnyService> {
    return apiService.put<AnyService>(`${this.baseEndpoint}/${id}`, data);
  }

  /**
   * Actualiza parcialmente un servicio
   */
  async patch(id: string, data: UpdateServiceDto): Promise<AnyService> {
    return apiService.patch<AnyService>(`${this.baseEndpoint}/${id}`, data);
  }

  /**
   * Elimina un servicio. Antes purga sus dominios personalizados (best-effort)
   * para limpiar el certificado ACM, la asociación del listener y el registro;
   * el backend de borrado del servicio no lo hace. Para servicios sin dominios
   * (la mayoría) el listado viene vacío y es un no-op.
   */
  async delete(id: string): Promise<void> {
    try {
      const domains = await this.getCustomDomains(id);
      if (domains.length > 0) {
        await Promise.allSettled(domains.map((d) => this.deleteCustomDomain(id, d.domainId)));
      }
    } catch {
      /* best-effort: si falla la purga de dominios, igual borramos el servicio */
    }
    return apiService.delete<void>(`${this.baseEndpoint}/${id}`);
  }

  /**
   * Inicia un servicio
   */
  async start(id: string): Promise<AnyService> {
    return apiService.post<AnyService>(`${this.baseEndpoint}/${id}/compute/start`);
  }

  /**
   * Detiene un servicio
   */
  async stop(id: string): Promise<AnyService> {
    return apiService.post<AnyService>(`${this.baseEndpoint}/${id}/compute/stop`);
  }

  /**
   * Reinicia un servicio (reinicia la EC2 completa)
   */
  async restart(id: string): Promise<AnyService> {
    return apiService.post<AnyService>(`${this.baseEndpoint}/${id}/compute/reboot`);
  }

  /**
   * Reinicia SOLO el motor (ej. mssql-server) vía SSM, sin reiniciar la EC2.
   * Mucho más rápido (~15s) y sin tumbar el resto de la máquina.
   */
  async restartEngine(id: string): Promise<{ ok: boolean; commandId?: string; unit?: string; message?: string }> {
    return apiService.post(`${this.baseEndpoint}/${id}/compute/restart-engine`);
  }

  /**
   * Obtiene servicios por tipo
   */
  async getByType(type: AnyService['type'], params?: {
    page?: number;
    limit?: number;
    status?: AnyService['status'];
  }): Promise<ServiceListResponse> {
    return this.getAll({ ...params, type });
  }

  /**
   * Obtiene servicios por categoría
   */
  async getByCategory(category: string, params?: {
    page?: number;
    limit?: number;
    status?: AnyService['status'];
  }): Promise<ServiceListResponse> {
    const queryParams = new URLSearchParams();

    queryParams.append('category', category);
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.status) queryParams.append('status', params.status);

    const queryString = queryParams.toString();
    const endpoint = `${this.baseEndpoint}?${queryString}`;

    return apiService.get<ServiceListResponse>(endpoint);
  }

  /**
   * Inicia una sesión de terminal para un servicio
   */
  async startSession(id: string): Promise<{
    sessionId: string;
    streamUrl: string;
    tokenValue: string;
  }> {
    return apiService.post<{
      sessionId: string;
      streamUrl: string;
      tokenValue: string;
    }>(`${this.baseEndpoint}/${id}/startsession`);
  }

  /**
   * Obtiene los eventos de un servicio
   */
  async getEvents(id: string): Promise<ServiceEventResponse[]> {
    return apiService.get<ServiceEventResponse[]>(`${this.baseEndpoint}/${id}/events`);
  }

  /**
   * Actualiza las reglas de seguridad de un servicio (agregar o eliminar)
   * @param id ID del servicio
   * @param mode Modo de operación: 'add' para agregar reglas, 'remove' para eliminar
   * @param direction Dirección: 'ingress' o 'egress'
   * @param rules Lista de reglas a agregar o eliminar
   */
  async updateSecurityRules(
    id: string,
    mode: 'add' | 'remove',
    direction: 'ingress' | 'egress',
    rules: Array<{
      protocol: string;
      fromPort: number;
      toPort: number;
      cidr: string;
      description?: string;
    }>
  ): Promise<void> {
    return apiService.post<void>(`${this.baseEndpoint}/${id}/compute/sg-rules`, {
      mode,
      direction,
      rules,
    });
  }

  /**
   * Actualiza las reglas de seguridad enviando dos objetos: uno para agregar y otro para eliminar
   * @param id ID del servicio
   * @param direction Dirección: 'ingress' o 'egress'
   * @param rulesToAdd Lista de reglas a agregar
   * @param rulesToRemove Lista de reglas a eliminar
   */
  /**
   * Lee las reglas de seguridad REALES del security group del servicio
   * (incluye las que creó CloudFormation al provisionar). GET vive en
   * platform_ec2_admin. Devuelve { ingress, egress } con reglas
   * { protocol, fromPort, toPort, cidr?, sourceSgId?, description }.
   */
  async getLiveSecurityRules(
    id: string
  ): Promise<{ ingress: any[]; egress: any[]; securityGroupId?: string | null }> {
    const resp = await apiService.get<{ ingress?: any[]; egress?: any[]; securityGroupId?: string | null }>(
      `${this.baseEndpoint}/${id}/compute/sg-rules`
    );
    return {
      ingress: Array.isArray(resp?.ingress) ? resp.ingress : [],
      egress: Array.isArray(resp?.egress) ? resp.egress : [],
      securityGroupId: resp?.securityGroupId ?? null,
    };
  }

  /**
   * Lista los puntos de recuperación (AWS Backup) de la instancia del servicio.
   * Vive en platform_ec2_admin. backupEnabled indica si el servicio tiene el
   * addon de respaldos activo (tag kubiy:backup=daily).
   */
  async getBackups(
    id: string
  ): Promise<{ backupEnabled: boolean; recoveryPoints: BackupRecoveryPoint[] }> {
    const resp = await apiService.get<{ backupEnabled?: boolean; recoveryPoints?: BackupRecoveryPoint[] }>(
      `${this.baseEndpoint}/${id}/compute/backups`
    );
    return {
      backupEnabled: !!resp?.backupEnabled,
      recoveryPoints: Array.isArray(resp?.recoveryPoints) ? resp.recoveryPoints : [],
    };
  }

  /** Lanza un respaldo on-demand (requiere el addon de respaldos). */
  async createBackup(id: string): Promise<{ ok: boolean; backupJobId?: string; message?: string }> {
    return apiService.post<{ ok: boolean; backupJobId?: string; message?: string }>(
      `${this.baseEndpoint}/${id}/compute/backups`
    );
  }

  async updateSecurityRulesBatch(
    id: string,
    direction: 'ingress' | 'egress',
    rulesToAdd: Array<{
      protocol: string;
      fromPort: number;
      toPort: number;
      cidr: string;
      description?: string;
    }>,
    rulesToRemove: Array<{
      protocol: string;
      fromPort: number;
      toPort: number;
      cidr: string;
      description?: string;
    }>
  ): Promise<void> {
    return apiService.post<void>(`${this.baseEndpoint}/${id}/compute/sg-rules`, [
      {
        mode: 'add',
        direction,
        rules: rulesToAdd,
      },
      {
        mode: 'remove',
        direction,
        rules: rulesToRemove,
      },
    ]);
  }

  /**
   * Guarda la lista completa de reglas de seguridad en base de datos
   * @param id ID del servicio
   * @param direction Dirección: 'ingress' o 'egress'
   * @param rules Lista completa de reglas a guardar
   */
  async saveSecurityRules(
    id: string,
    direction: 'ingress' | 'egress',
    rules: Array<{
      protocol: string;
      fromPort: number;
      toPort: number;
      cidr: string;
      description?: string;
    }>
  ): Promise<void> {
    return apiService.patch<void>(`${this.baseEndpoint}/${id}/compute/sg-rules`, {
      direction,
      rules,
    });
  }

  /**
   * Descarga las credenciales de Windows para un servicio
   * @param id ID del servicio
   */
  async downloadWindowsCredentials(id: string): Promise<Blob> {
    return apiService.postBlob(`${this.baseEndpoint}/${id}/compute/password`);
  }

  /**
   * Descarga la clave SSH para conectarse a un servicio Linux
   * @param id ID del servicio
   */
  async downloadLinuxSshKey(id: string): Promise<Blob> {
    return apiService.postBlob(`${this.baseEndpoint}/${id}/compute/ssh`);
  }

  /**
   * Obtiene las credenciales master de una base de datos
   * @param id ID del servicio (base de datos)
   */
  async getDbMasterCredentials(id: string): Promise<{ username?: string; password?: string }> {
    return apiService.post<{ username?: string; password?: string }>(`${this.baseEndpoint}/${id}/db/credentials`, {});
  }

  /**
   * Actualiza las IPs permitidas de una base de datos
   * @param id ID del servicio (base de datos)
   * @param allowedCidrs Lista de CIDR permitidos (ej: ['189.203.1.2/32', '10.0.0.0/16'])
   */
  async updateDbAllowedIPs(id: string, allowedCidrs: string[]): Promise<void> {
    return apiService.post<void>(`${this.baseEndpoint}/${id}/db/allow-ips`, { allowedCidrs });
  }

  /**
   * Permite que un servicio se conecte a una base de datos
   * @param id ID del servicio (base de datos)
   * @param payload source: ID del servicio origen; sourceTenant: tenant del servicio origen (manual)
   */
  async allowDbServiceSource(
    id: string,
    payload: { source: string; sourceTenant?: string }
  ): Promise<void> {
    return apiService.post<void>(`${this.baseEndpoint}/${id}/allow-service`, payload);
  }

  /**
   * Revoca el permiso de un servicio para conectarse a una base de datos
   * @param id ID del servicio (base de datos)
   * @param payload source: ID del servicio origen; sourceTenant: tenant del servicio origen (si aplica)
   */
  async revokeDbServiceSource(
    id: string,
    payload: { source: string; sourceTenant?: string }
  ): Promise<void> {
    return apiService.delete<void>(`${this.baseEndpoint}/${id}/allow-service`, payload);
  }

  /**
   * Inicia una base de datos
   */
  async dbStart(id: string): Promise<AnyService> {
    return apiService.post<AnyService>(`${this.baseEndpoint}/${id}/db/start`);
  }

  /**
   * Detiene una base de datos
   */
  async dbStop(id: string): Promise<AnyService> {
    return apiService.post<AnyService>(`${this.baseEndpoint}/${id}/db/stop`);
  }

  /**
   * Reinicia una base de datos
   */
  async dbRestart(id: string): Promise<AnyService> {
    return apiService.post<AnyService>(`${this.baseEndpoint}/${id}/db/restart`);
  }

  /**
   * Genera una nueva API key para un servicio
   * @param appId ID de la aplicación
   * @param serviceId ID del servicio (se envía en el body)
   * @param name Nombre de la API key
   * @param expirationDays Días de expiración (0 para ilimitado, undefined para sin expiración)
   */
  async generateApiKey(appId: string, serviceId: string, name: string, expirationDays?: number): Promise<ApiKey> {
    const payload: any = { serviceId, name };
    if (expirationDays !== undefined) {
      payload.expirationDays = expirationDays;
    }
    return apiService.post<ApiKey>(`/apps/${appId}/tokens`, payload);
  }

  /**
   * Revoca una API key
   */
  async revokeApiKey(serviceId: string, apiKeyId: string): Promise<void> {
    return apiService.delete(`${this.baseEndpoint}/apikey/${apiKeyId}`);
  }

  /**
   * Obtiene las API keys de una aplicación
   * @param appId ID de la aplicación
   */
  async getApiKeys(appId: string): Promise<ApiKey[]> {
    return apiService.get<ApiKey[]>(`/apps/${appId}/tokens`);
  }

  /**
   * Monitorea el estado de un servicio hasta que alcance un estado autorizado
   * @param id ID del servicio
   * @param authorizedStatuses Estados autorizados que detienen el monitoreo
   * @param onStatusUpdate Callback que se ejecuta cada vez que se actualiza el estado
   * @param maxWaitTime Tiempo máximo de espera en milisegundos (default: 5 minutos)
   * @param pollInterval Intervalo de polling en milisegundos (default: 3 segundos)
   * @returns Promise que se resuelve cuando el servicio alcanza un estado autorizado
   */
  async monitorServiceStatus(
    id: string,
    authorizedStatuses: string[] = ['running', 'stopped', 'deleted'],
    onStatusUpdate?: (service: AnyService) => void,
    maxWaitTime: number = 5 * 60 * 1000, // 5 minutos
    pollInterval: number = 3000 // 3 segundos
  ): Promise<AnyService> {
    const startTime = Date.now();

    return new Promise((resolve, reject) => {
      const poll = async () => {
        try {
          // Verificar timeout
          if (Date.now() - startTime > maxWaitTime) {
            reject(new Error('Timeout: El servicio no alcanzó un estado autorizado en el tiempo esperado'));
            return;
          }

          // Obtener el estado actual del servicio
          const service = await this.getById(id);

          // Normalizar el estado
          const normalizedStatus = (service.status || '').toLowerCase().trim();

          // Si el estado está autorizado, resolver
          if (authorizedStatuses.some(status => normalizedStatus === status.toLowerCase().trim())) {
            // Asegurar que el servicio tenga el estado normalizado
            const serviceWithNormalizedStatus = {
              ...service,
              status: normalizedStatus as any,
            };
            resolve(serviceWithNormalizedStatus);
            return;
          }

          // Si el servicio fue eliminado (404), considerar como 'deleted'
          if (normalizedStatus === 'deleted' || normalizedStatus === 'terminated') {
            const serviceWithNormalizedStatus = {
              ...service,
              status: 'deleted' as any,
            };
            resolve(serviceWithNormalizedStatus);
            return;
          }

          // Notificar actualización del estado (con estado normalizado)
          if (onStatusUpdate) {
            const serviceWithNormalizedStatus = {
              ...service,
              status: normalizedStatus as any,
            };
            onStatusUpdate(serviceWithNormalizedStatus);
          }

          // No obtener eventos aquí para evitar llamadas duplicadas
          // Los eventos se obtendrán desde el componente cuando sea necesario

          // Continuar monitoreando
          setTimeout(poll, pollInterval);
        } catch (error: any) {
          // Si el servicio no existe (404), considerar como eliminado
          if (error?.response?.status === 404 || error?.status === 404) {
            // Crear un objeto de servicio con estado deleted
            const deletedService = {
              id,
              status: 'deleted' as const,
            } as AnyService;
            resolve(deletedService);
            return;
          }

          // Para otros errores, continuar monitoreando (puede ser temporal)
          console.warn('Error al monitorear servicio:', error);
          setTimeout(poll, pollInterval);
        }
      };

      // Iniciar el polling
      poll();
    });
  }


  async updateTemplate(serviceId: string, html: string): Promise<void> {
    return apiService.patch<void>(`${this.baseEndpoint}/${serviceId}/template`, {
      html
    });
  }

  async getTemplate(serviceId: string): Promise<string> {
    return apiService.getText(`${this.baseEndpoint}/${serviceId}/template`);
  }

  async exchangeGithubCode(code: string): Promise<{ access_token: string; token_type: string; scope: string; }> {
    try {
      const result = await apiService.postWithoutAuth<{
        access_token: string;
        token_type: string;
        scope: string;
      }>('/github/oauth/exchange', { code });

      return result;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Obtiene los repositorios de GitHub del usuario
   * @param appId ID de la aplicación
   */
  async getGithubRepos(appId: string): Promise<Array<{ id: string; name: string; full_name: string; private: boolean; html_url: string; description: string; default_branch: string; }>> {
    return apiService.post<Array<{
      id: string;
      name: string;
      full_name: string;
      private: boolean;
      html_url: string;
      description: string;
      default_branch: string;
    }>>(`/apps/${appId}/github/repos`, {});
  }

  /**
   * Configura el deploy automático de GitHub para una app
   * @param appId ID de la aplicación
   * @param config Configuración del deploy
   */
  async configureGithubDeploy(appId: string, config: { repoId: string; repoFullName: string; branch: string; startMode?: string; targetMode?: string; }): Promise<{ webhookId: string; webhookUrl: string; port: number; message: string; commandId: string; }> {
    return apiService.post<{
      webhookId: string;
      webhookUrl: string;
      port: number;
      message: string;
      commandId: string;
    }>(`/apps/${appId}/github/configure`, config);
  }

  /**
   * Desconecta GitHub de una app
   * @param appId ID de la aplicación
   */
  async disconnectGithub(appId: string): Promise<void> {
    return apiService.post(`/apps/${appId}/github/disconnect`, {});
  }

  /**
   * Obtiene la configuración de GitHub de una app
   * @param appId ID de la aplicación
   */
  async getGithubConfig(appId: string): Promise<{ connected: boolean; repoFullName?: string; branch?: string; startMode?: string; targetMode?: string; lastDeploy?: { commit: string; message: string; author: string; timestamp: string; status: 'success' | 'failed' | 'pending'; }; initialDeploy?: { status: string; commandId: string; timestamp: string; }; } | null> {
    return apiService.get<any>(`/apps/${appId}/github/config`);
  }

  /**
   * Obtiene el historial de deploys de una app
   * @param appId ID de la aplicación
   * @param limit Límite de resultados
   */
  async getGithubDeployHistory(appId: string, limit: number = 10): Promise<Array<{ id: string; commit: string; message: string; author: string; timestamp: string; status: 'success' | 'failed' | 'pending'; duration?: number; error?: string; }>> {
    return apiService.get<Array<any>>(`/apps/${appId}/github/deploys?limit=${limit}`);
  }

  /**
   * Dispara un deploy manual para una app
   * @param appId ID de la aplicación
   */
  async triggerGithubDeploy(appId: string): Promise<{ deployId: string; message: string; }> {
    return apiService.post<{
      deployId: string;
      message: string;
    }>(`/apps/${appId}/github/deploy`);
  }


  /**
   * Obtiene el estado del deploy de una app
   * @param appId ID de la aplicación
   */
  async getGithubDeployStatus(appId: string): Promise<{ status: 'success' | 'failed' | 'pending' | 'in_progress' | 'no_deploys' | 'unknown'; ssmStatus?: string; lastDeploy?: any; message?: string; }> {
    return apiService.get<{
      status: 'success' | 'failed' | 'pending' | 'in_progress' | 'no_deploys' | 'unknown';
      ssmStatus?: string;
      lastDeploy?: any;
      message?: string;
    }>(`/apps/${appId}/github/deploy/status`);
  }

  /**
   * Crea una nueva app dentro de un servicio existente (POST /apps/:serviceId)
   * @param serviceId ID del servicio
   * @param payload Objeto con appConfig (misma estructura que orderData.metadata.appConfig en el alta)
   */
  async createApp(serviceId: string, payload: {
    name: string;
    description?: string;
    subdomain?: string;
    /** Slug para la URL: https://{slug}.{subdomain}.apps1.kubiy.site */
    slug?: string;
    type?: string;
    environment: string;
    autoDeploy?: boolean;
    premiumLogs?: boolean;
    premiumLogsPlan?: string | null;
    environmentVariables?: Array<{ name: string; value: string; hidden?: boolean }>;
    deploymentType?: string;
    runtime?: { technology: string; version: string };
    startMode: string;
    targetMode: string;
  }): Promise<any> {
    return apiService.post<any>(`/apps/${serviceId}`, payload);
  }

  /**
   * Verifica si un subdominio está disponible para App Deploy
   * @param name Nombre del subdominio (ej: mi-app)
   * @param appType Tipo de app: 'static' o 'server'
   */
  async checkSubdomainAvailable(name: string, appType: string): Promise<{ available: boolean }> {
    const response = await apiService.post<{ available: boolean }>('/apps/subdomain/available', {
      name: name.trim(),
      appType,
    });
    return response ?? { available: false };
  }

  /**
   * Obtiene la información de una aplicación
   * @param appId ID de la aplicación
   */
  async getAppById(appId: string): Promise<any> {
    return apiService.get<any>(`/apps/${appId}`);
  }

  /**
   * Actualiza las variables de entorno de una aplicación
   * @param appId ID de la aplicación
   * @param vars Lista de variables de entorno
   */
  async updateEnvironmentVars(
    appId: string,
    vars: Array<{ name: string; value: string; hidden?: boolean }>
  ): Promise<void> {
    return apiService.patch<void>(`/apps/${appId}/environment-vars`, {
      vars
    });
  }

  async saveGithubToken(appId: string, token: string, tenantId: string): Promise<void> {
    return apiService.post<void>(`/apps/${appId}/github/token`, { token, tenantId });
  }

  async getDeployHistory(appId: string, limit: number = 20, nextToken?: string): Promise<DeployHistoryResponse> {
    const params = new URLSearchParams();
    params.append('limit', limit.toString());
    if (nextToken) params.append('nextToken', nextToken);
    const qs = params.toString();
    return apiService.get<DeployHistoryResponse>(`/apps/${appId}/deploy/history${qs ? `?${qs}` : ''}`);
  }

  /**
   * Rollback de una app a un deploy anterior
   * POST /apps/{appId}/rollback
   * @param appId ID de la aplicación
   * @param payload serviceId, appId, deployId
   */
  async rollbackAppDeploy(
    appId: string,
    payload: { serviceId: string; appId: string; deployId: string }
  ): Promise<any> {
    return apiService.post<any>(`/apps/${appId}/rollback`, payload);
  }

  async getAppLogs(appId: string, type: string = 'deploy', lines: number = 200): Promise<AppLogsResponse> {
    const params = new URLSearchParams();
    params.append('type', type);
    params.append('lines', lines.toString());
    return apiService.get<AppLogsResponse>(`/apps/${appId}/logs?${params.toString()}`);
  }

  /**
   * Control del proceso de una app (systemd via SSM)
   * POST /apps/{appId}/process
   * @param appId ID de la aplicación
   * @param action restart | stop | start | status
   */
  async appProcessAction(
    appId: string,
    action: 'restart' | 'stop' | 'start' | 'status'
  ): Promise<{ appId: string; action: string; success: boolean; process?: any; error?: string }> {
    return apiService.post(`/apps/${appId}/process`, { action });
  }

  /**
   * Solicita una presigned URL para subir un ZIP de deploy estático
   * POST /apps/{appId}/deploy/upload
   * @param appId ID de la aplicación
   * @param serviceId ID del servicio
   * @param message Mensaje descriptivo del deploy
   * @param env Ambiente (default: production)
   */
  async requestStaticDeployUpload(
    appId: string,
    serviceId: string,
    message?: string,
    env?: string,
    runtime?: string
  ): Promise<{
    deployId: string;
    releaseId: string;
    uploadUrl: string;
    expiresIn: number;
    key: string;
    prefix: string;
    status: string;
  }> {
    return apiService.post<{
      deployId: string;
      releaseId: string;
      uploadUrl: string;
      expiresIn: number;
      key: string;
      prefix: string;
      status: string;
    }>(`/apps/${appId}/deploy/upload`, {
      serviceId,
      commitMessage: message || 'Manual ZIP upload',
      env: env || 'production',
      runtime: runtime || undefined,
    });
  }

  /**
   * Sube un archivo ZIP directamente a S3 usando la presigned URL
   * @param uploadUrl Presigned URL devuelta por requestStaticDeployUpload
   * @param file Archivo ZIP a subir
   * @param onProgress Callback de progreso (0-100)
   */
  async uploadZipToS3(
    uploadUrl: string,
    file: File,
    onProgress?: (percent: number) => void
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', uploadUrl, true);
      xhr.setRequestHeader('Content-Type', 'application/zip');

      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && onProgress) {
          const percent = Math.round((e.loaded / e.total) * 100);
          onProgress(percent);
        }
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      };

      xhr.onerror = () => reject(new Error('Upload failed: network error'));
      xhr.ontimeout = () => reject(new Error('Upload failed: timeout'));

      xhr.send(file);
    });
  }

  async getMqCredentials(id: string): Promise<{ username?: string; password?: string }> {
    return apiService.post<{ username?: string; password?: string }>(
      `${this.baseEndpoint}/${id}/mq/credentials`, {}
    );
  }

  async getStrapiDbCredentials(id: string): Promise<{ username?: string; password?: string }> {
    return apiService.post<{ username?: string; password?: string }>(
      `${this.baseEndpoint}/${id}/strapi/db-credentials`, {}
    );
  }

  async getMedusaDbCredentials(id: string): Promise<{ username?: string; password?: string }> {
    return apiService.post<{ username?: string; password?: string }>(
      `${this.baseEndpoint}/${id}/medusa/db-credentials`, {}
    );
  }

  async getMedusaCredentials(id: string): Promise<{ email?: string; password?: string }> {
    return apiService.post<{ email?: string; password?: string }>(
      `${this.baseEndpoint}/${id}/medusa/credentials`, {}
    );
  }

  async getOnyxDbCredentials(id: string): Promise<{ username?: string; password?: string }> {
    return apiService.post<{ username?: string; password?: string }>(
      `${this.baseEndpoint}/${id}/onyx/db-credentials`, {}
    );
  }

  async getOdooCredentials(id: string): Promise<{ email?: string; password?: string }> {
    return apiService.post<{ email?: string; password?: string }>(
      `${this.baseEndpoint}/${id}/odoo/credentials`, {}
    );
  }

  async getOdooDbCredentials(id: string): Promise<{ username?: string; password?: string }> {
    return apiService.post<{ username?: string; password?: string }>(
      `${this.baseEndpoint}/${id}/odoo/db-credentials`, {}
    );
  }

  async getMetabaseCredentials(id: string): Promise<{ email?: string; password?: string }> {
    return apiService.post<{ email?: string; password?: string }>(
      `${this.baseEndpoint}/${id}/metabase/credentials`, {}
    );
  }

  async getMetabaseDbCredentials(id: string): Promise<{ username?: string; password?: string }> {
    return apiService.post<{ username?: string; password?: string }>(
      `${this.baseEndpoint}/${id}/metabase/db-credentials`, {}
    );
  }

  async getClickhouseCredentials(id: string): Promise<{ username?: string; password?: string }> {
    return apiService.post<{ username?: string; password?: string }>(
      `${this.baseEndpoint}/${id}/clickhouse/credentials`, {}
    );
  }

  async getIdurarCredentials(id: string): Promise<{ email?: string; password?: string }> {
    return apiService.post<{ email?: string; password?: string }>(
      `${this.baseEndpoint}/${id}/idurar/credentials`, {}
    );
  }

  async getFrappeEducationCredentials(id: string): Promise<{ email?: string; password?: string }> {
    return apiService.post<{ email?: string; password?: string }>(
      `${this.baseEndpoint}/${id}/frappe-education/credentials`, {}
    );
  }

  async getTooljetDbCredentials(id: string): Promise<{ username?: string; password?: string }> {
    return apiService.post<{ username?: string; password?: string }>(
      `${this.baseEndpoint}/${id}/tooljet/db-credentials`, {}
    );
  }

  async getGhostCredentials(id: string): Promise<{ email?: string; password?: string }> {
    return apiService.post<{ email?: string; password?: string }>(
      `${this.baseEndpoint}/${id}/ghost/credentials`, {}
    );
  }

  async getGhostDbCredentials(id: string): Promise<{ username?: string; password?: string }> {
    return apiService.post<{ username?: string; password?: string }>(
      `${this.baseEndpoint}/${id}/ghost/db-credentials`, {}
    );
  }

  async getRedisCredentials(id: string): Promise<{ username?: string; password?: string }> {
    return apiService.post<{ username?: string; password?: string }>(
      `${this.baseEndpoint}/${id}/redis/credentials`, {}
    );
  }

  async getWordPressCredentials(id: string): Promise<{ email?: string; password?: string }> {
    return apiService.post<{ email?: string; password?: string }>(
      `${this.baseEndpoint}/${id}/wordpress/credentials`, {}
    );
  }

  async getWordPressDbCredentials(id: string): Promise<{ username?: string; password?: string }> {
    return apiService.post<{ username?: string; password?: string }>(
      `${this.baseEndpoint}/${id}/wordpress/db-credentials`, {}
    );
  }

  async getSqlServerCredentials(id: string): Promise<{ username?: string; password?: string }> {
    return apiService.post<{ username?: string; password?: string }>(
      `${this.baseEndpoint}/${id}/sqlserver/credentials`, {}
    );
  }

  /**
   * Obtiene métricas de CloudWatch para un servicio (EC2 o RDS).
   * Requiere que el path GET /services/{id}/metrics esté registrado en API Gateway.
   */
  async getMetrics(
    serviceId: string,
    rangeHours: number = 1,
    periodSeconds: number = 300,
  ): Promise<ServiceMetrics> {
    const qp = new URLSearchParams();
    qp.append('range', rangeHours.toString());
    qp.append('period', periodSeconds.toString());
    return apiService.get<ServiceMetrics>(`${this.baseEndpoint}/${serviceId}/metrics?${qp}`);
  }

  async upgradeOdooEnterprise(serviceId: string, githubToken: string) {
    return apiService.post(
      `${this.baseEndpoint}/${serviceId}/odoo/upgrade-enterprise`,
      { githubToken }
    );
  }

  // ============================================================
  // Custom Domains (App Deploy)
  // ------------------------------------------------------------
  // - GET  /services/{serviceId}/custom-domain/{appId}  → lista los dominios de UNA app
  // - POST /services/{serviceId}/register-custom-domain → registra un dominio (body: { appId, domainName })
  // El verify se resuelve haciendo re-fetch del listado.
  // El endpoint de delete aún no está definido en la API; queda placeholder.
  // ============================================================

  /**
   * Lista los dominios personalizados registrados para una app específica de un servicio.
   * El backend devuelve `{ count, items }`.
   */
  async getCustomDomains(serviceId: string, appId?: string): Promise<CustomDomain[]> {
    // appId es opcional: los servicios de marketplace EC2 no tienen "app" de
    // app-deploy. El backend lista por serviceId; el segmento de ruta se rellena
    // con un placeholder cuando no hay appId.
    const appSegment = appId || 'marketplace';
    const response = await apiService.get<CustomDomainsListResponse | CustomDomain[]>(
      `${this.baseEndpoint}/${serviceId}/custom-domain/${appSegment}`
    );

    let items: CustomDomain[] = [];
    if (Array.isArray(response)) items = response;
    else if (response && Array.isArray(response.items)) items = response.items;

    return items.map((item) => normalizeCustomDomain(item));
  }

  /**
   * Registra un dominio personalizado para una app específica del servicio.
   * El backend responde con el `domainId`, las `dnsInstructions` (cert + traffic)
   * y un `message` informativo que la UI puede mostrar al usuario.
   */
  async registerCustomDomain(
    serviceId: string,
    payload: { appId?: string; domainName: string }
  ): Promise<CustomDomain> {
    // Sin appId => el backend trata el registro como servicio de marketplace.
    const body: Record<string, string> = {
      domainName: payload.domainName.trim().toLowerCase(),
    };
    if (payload.appId) body.appId = payload.appId;
    const created = await apiService.post<CustomDomainRaw>(
      `${this.baseEndpoint}/${serviceId}/custom-domain/register`,
      body
    );
    return normalizeCustomDomain(created);
  }

  /**
   * Vuelve a verificar el estado de un dominio personalizado.
   *
   * POST /services/{serviceId}/custom-domain/verify   body: { appId, domainId }
   *
   * Devuelve el dominio actualizado. Si el backend respondiera con un
   * formato distinto (por ejemplo `{ item }` o sólo un status), hacemos
   * fallback a refrescar el listado y devolver el dominio correspondiente.
   */
  async verifyCustomDomain(
    serviceId: string,
    appId: string | undefined,
    domainId: string,
    existing?: CustomDomain | null
  ): Promise<CustomDomain | null> {
    const verifyBody: Record<string, string> = { domainId };
    if (appId) verifyBody.appId = appId;
    const response = await apiService.post<CustomDomainRaw | { item?: CustomDomainRaw }>(
      `${this.baseEndpoint}/${serviceId}/custom-domain/verify`,
      verifyBody
    );

    let raw: CustomDomainRaw | null = null;
    if (response && typeof response === 'object') {
      if ('domainId' in response && response.domainId) raw = response;
      else if ('item' in response && response.item?.domainId) raw = response.item;
    }

    if (!raw) {
      const list = await this.getCustomDomains(serviceId, appId);
      const fresh = list.find((d) => d.domainId === domainId) ?? null;
      return fresh ? mergeCustomDomain(existing ?? fresh, fresh) : null;
    }

    let normalized = normalizeCustomDomain(raw, existing ?? null);
    const verifyMessage = raw.message;

    const statusUpper = (normalized.status || '').toUpperCase();
    const trafficMissing = !normalized.dnsInstructions?.traffic?.value;
    const shouldRefreshList =
      statusUpper === 'PENDING_TRAFFIC_DNS' || (trafficMissing && statusUpper !== 'ACTIVE');

    if (shouldRefreshList) {
      const list = await this.getCustomDomains(serviceId, appId);
      const fresh = list.find((d) => d.domainId === domainId);
      if (fresh) {
        normalized = mergeCustomDomain(normalized, fresh);
        if (verifyMessage) normalized.message = verifyMessage;
      }
    }

    return normalized;
  }

  /**
   * Elimina un dominio personalizado del servicio.
   * TODO: confirmar el endpoint con el backend. Probable: DELETE /services/{id}/custom-domain/{domainId}
   */
  async deleteCustomDomain(serviceId: string, domainId: string): Promise<void> {
    return apiService.delete<void>(`${this.baseEndpoint}/${serviceId}/custom-domain/${domainId}`);
  }

  async changePlan(serviceId: string, plan: string): Promise<{
    serviceId: string;
    previousPlan?: string;
    previousInstanceType?: string;
    newPlan: string;
    newInstanceType: string;
    stackId: string;
    status: string;
    estimatedDowntime: string;
    message: string;
  }> {
    return apiService.post(`${this.baseEndpoint}/${serviceId}/plan`, { plan });
  }

}

/**
 * Interfaz para eventos de servicio (respuesta del API)
 */
export interface ServiceEventResponse {
  stackId?: string;
  tenantId: string;
  ttl?: number;
  createdAt: string;
  source?: string;
  message: string; // Mensaje del evento
  level?: 'INFO' | 'WARNING' | 'ERROR' | 'SUCCESS'; // Nivel del evento
  phase?: string; // Fase del proceso (GENERAL, SECURITY, COMPUTE, etc.)
  description?: string; // Descripción adicional del evento
  detailType?: string;
  stackName?: string;
  serviceId: string;
  stackStatus?: string;
  PK?: string;
  type?: string;
  statusReason?: string | null;
  SK?: string;
  // Campos adicionales para recursos
  resourceStatus?: string;
  resourceType?: string;
  logicalResourceId?: string;
  physicalResourceId?: string;
}

/**
 * Interfaz para eventos de servicio (formato interno de la UI)
 */
export interface ServiceEvent {
  id: string;
  timestamp: Date;
  type: 'info' | 'warning' | 'error' | 'success';
  message: string;
  details?: string;
  source?: string;
  isKubiyEvent?: boolean;
  isDeletionEvent?: boolean;
}

// Instancia singleton del servicio
export const servicesService = new ServicesService();

